import { INGLIS_BLUE } from '../../constants/colors'
import { useState, useEffect, useRef, type CSSProperties, type KeyboardEvent } from 'react'
import { useMapContext } from '../../context/MapContext'
import { speakSiteInfo, cancelSiteAudio } from '../../utils/audioUtils'
import { useSitePhoto } from '../../hooks/useSitePhoto'
import { useWindowWidth } from '../../hooks/useWindowWidth'
import { MARKER_CONFIG } from '../../constants/markerConfig'
import { getReadableTextColor } from '../../utils/contrast'
import FamilyContent from './FamilyContent'
import { getSpokenDescription, type Site, type ReuseSite } from '../../types'
import { spokenLabel } from '../../constants/mapConfig'
import SpokenAT from '../SpokenAT'

const PinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
)
const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
    <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/>
  </svg>
)

/**
 * Photo strip at the top of the panel — and the colored fallback block that stands in for it, so
 * the two always occupy the same box.
 *
 * Was a flat `height: 180` with `object-fit: cover`, which is a ~2.3:1 box at the fixed 416px
 * docked width and a ~3.1:1 one once the panel renders `fluidWidth` inside a widened list panel —
 * either way, cover cut the difference out of the photo and people in the frame lost their heads.
 * The photos in public/photos are nowhere near a single aspect ratio (they run from 988×1200
 * portrait and 360×360 square up to 1200×409), so no fixed box can cover-crop all of them safely;
 * `object-fit: contain` over a light background letterboxes the odd ones instead of cutting them.
 * The 16:9 aspectRatio then sizes the box off the panel width (most photos are close to landscape,
 * so they nearly fill it), maxHeight stops a wide panel from spending half the viewport on the
 * photo, and minHeight keeps the fallback label's block from collapsing on narrow phones.
 */
const photoStripStyle: CSSProperties = {
  width: '100%',
  aspectRatio: '16 / 9',
  minHeight: 160,
  maxHeight: 280,
  flexShrink: 0,
}

const FOCUSABLE_SELECTOR ='a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

interface SitePanelProps {
  site?: Site | null
  fluidWidth?: boolean
}

export default function SitePanel({ site: siteProp, fluidWidth = false }: SitePanelProps) {
  const { selectedSite, setSelectedSite, audioGuide } = useMapContext()
  const site = siteProp ?? selectedSite
  const [forceHide, setForceHide] = useState(false)
  const [expandedSubSites, setExpandedSubSites] = useState<Set<string>>(new Set())

  function toggleSubSite(name: string) {
    setExpandedSubSites(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const { src, hasFallback, onError } = useSitePhoto(site?.id ?? '')
  const siteConfig = site ? MARKER_CONFIG[site.category] : null

  const asideRef = useRef<HTMLElement>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)

  // Whether this panel is currently acting as a modal overlay rather than a docked side panel.
  // The `!fluidWidth` + narrow-viewport combination is exactly when the `fixed inset-0` class
  // below covers the whole screen (see the className ternary — `md:static md:inset-auto` drops
  // that at the md breakpoint). Everything modal-flavoured keys off this single value: the dialog
  // role, aria-modal, the Tab trap and Escape-to-close. Driven by useWindowWidth (not a cached
  // matchMedia read) so a rotation or resize mid-session re-evaluates on the next render, and so
  // the rendered ARIA and the keyboard behaviour can never disagree about which mode we're in.
  const windowWidth = useWindowWidth()
  const isModal = !fluidWidth && windowWidth < 768

  const [prevSiteId, setPrevSiteId] = useState(site?.id)
  if (prevSiteId !== site?.id) {
    setPrevSiteId(site?.id)
    setForceHide(false)
  }

  // Known, accepted overlap: a user running both a screen reader AND this audio guide gets two
  // voices talking at once here (the SR announces the panel via the focus-move effect below,
  // while this speaks the same content via TTS). There's no reliable way to detect an active
  // screen reader from JS to suppress one automatically. Left as-is deliberately — this feature
  // targets low-vision users who want things read aloud without necessarily running a full
  // screen reader; a screen-reader-dependent user simply wouldn't turn the audio guide on.
  useEffect(() => {
    if (!audioGuide || !site) return
    const desc = getSpokenDescription(site.audiences)
    speakSiteInfo(site, desc, () => {})
    // Scoped to this site's id so the cleanup can't silence a marker hover that has already taken
    // over — this effect re-runs on every site change, well after the pointer has moved on.
    return () => cancelSiteAudio(site.id)
  }, [site, audioGuide])

  // Android's hardware/gesture Back closes this panel, which is what every platform convention
  // says a full-screen modal should do. Without a history entry, Back left the page entirely —
  // and in installed-PWA mode (display: standalone, see the manifest) it exited the app, throwing
  // away the user's filters and map position. It matters for accessibility too: Back is a large,
  // always-present, system-level control, far easier to hit than a small ✕ for anyone with a motor
  // impairment or a tremor.
  //
  // Scoped to the modal case only. As a docked side panel there is no overlay to back out of, and
  // hijacking Back there would mean the gesture stopped doing what the rest of the page implies.
  useEffect(() => {
    if (!isModal || !site) return
    window.history.pushState({ inglisSitePanel: true }, '')
    const closeOnBack = () => {
      setSelectedSite(null)
      setForceHide(true)
    }
    window.addEventListener('popstate', closeOnBack)
    return () => {
      window.removeEventListener('popstate', closeOnBack)
      // Closed some other way (✕ / Escape / a new selection): consume the entry we pushed, so the
      // user doesn't have to press Back twice to actually leave the page. After a real Back the
      // entry is already gone, so this guard is what stops a double-navigation.
      if ((window.history.state as { inglisSitePanel?: boolean } | null)?.inglisSitePanel) {
        window.history.back()
      }
    }
  }, [isModal, site, setSelectedSite])

  // Move focus into the panel when it opens (e.g. a marker click selects a site), and remember
  // what had focus so we can restore it on close — otherwise keyboard/screen-reader users lose
  // their place when this mounts as a full-screen overlay on mobile.
  useEffect(() => {
    if (!site) return
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null
    asideRef.current?.focus()
  }, [site])

  if (forceHide || !site) return null

  function handleClose() {
    setSelectedSite(null)
    setForceHide(true)
    previouslyFocusedRef.current?.focus()
  }

  function handleKeyDown(e: KeyboardEvent<HTMLElement>) {
    // Escape closes, but only in the modal case — as a docked side panel the map and list beside
    // it are still usable and Escape belongs to whatever the user is actually working in. Paired
    // with the dialog semantics below: announcing something as a dialog and then not honouring
    // Escape is its own trap.
    if (e.key === 'Escape' && isModal) {
      e.stopPropagation()
      handleClose()
      return
    }
    if (e.key !== 'Tab') return
    if (!isModal) return
    const container = asideRef.current
    if (!container) return
    const focusables = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  function renderSubSiteItem(item: ReuseSite) {
    const isExpanded = expandedSubSites.has(item.name)
    const hasDetail = Boolean(item.description || item.highlights?.length || item.cta)
    const label = (
      <span style={{ fontSize: '0.85rem', color: 'var(--text-body)' }}>
        <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}><SpokenAT text={item.name} /></span>
        {(item.phone || item.email) && (
          <span style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
            {/* tel:/mailto: links rather than plain text — lets AT users activate them directly
                (e.g. "call" via screen reader rotor) and mobile users tap to dial/compose instead
                of having to copy the number/address by hand. */}
            {item.phone && (
              <a href={`tel:${item.phone.replace(/[^\d+]/g, '')}`} style={{ color: 'inherit' }}>
                {item.phone}
              </a>
            )}
            {item.phone && item.email ? ' · ' : ''}
            {item.email && (
              <a href={`mailto:${item.email}`} style={{ color: 'inherit' }}>
                {item.email}
              </a>
            )}
          </span>
        )}
      </span>
    )
    return (
      <li key={item.name} style={{ paddingBottom: 8, borderBottom: '1px solid #f5f6f8' }}>
        {hasDetail ? (
          <button
            type="button"
            onClick={() => toggleSubSite(item.name)}
            aria-expanded={isExpanded}
            className="subsite-toggle-btn"
          >
            {label}
            <span aria-hidden="true" style={{ color: 'var(--text-faint)', fontSize: '0.75rem', flexShrink: 0, marginTop: 2 }}>
              {isExpanded ? '▾' : '▸'}
            </span>
          </button>
        ) : (
          <div style={{ display: 'flex', width: '100%', boxSizing: 'border-box', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            {label}
          </div>
        )}
        {isExpanded && hasDetail && (
          <div style={{ marginTop: 8, paddingLeft: 2 }}>
            {item.description && (
              <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 8px' }}><SpokenAT text={item.description} /></p>
            )}
            {item.highlights && item.highlights.length > 0 && (
              <ul style={{ listStyle: 'none', margin: '0 0 8px', padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {item.highlights.map((h, i) => (
                  <li key={`${i}-${h}`} style={{ fontSize: '0.8rem', color: 'var(--text-body)', display: 'flex', gap: 6 }}>
                    <span aria-hidden="true">•</span><SpokenAT text={h} />
                  </li>
                ))}
              </ul>
            )}
            {item.cta && (
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}><SpokenAT text={item.cta} /></p>
            )}
          </div>
        )}
      </li>
    )
  }

  const asideStyle: CSSProperties = fluidWidth
    ? { flexGrow: 1, overflowY: 'auto', boxShadow: '0 2px 4px rgba(16,28,48,.04), 0 8px 28px rgba(16,28,48,.10)' }
    : { overflowY: 'auto', boxShadow: '0 2px 4px rgba(16,28,48,.04), 0 8px 28px rgba(16,28,48,.10)' }

  return (
    // This is a focus-trap keydown handler on the modal container itself (see handleKeyDown
    // above), not an interaction meant to be discoverable/operable as part of this element's own
    // role — it only intercepts Tab to keep focus cycling within an already-focused, already-open
    // panel.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <aside
      ref={asideRef}
      tabIndex={-1}
      // Modal-only dialog semantics. Without these the panel covered the entire viewport and
      // trapped Tab while still presenting as a plain <aside>: a screen reader user got a trap
      // with no explanation, and — because nothing constrained the virtual cursor — could still
      // swipe into the map, list and top bar sitting *behind* the overlay, i.e. content that is
      // visually covered and unreachable by Tab. aria-modal is what tells AT to confine itself to
      // this subtree, bringing swipe/virtual-cursor navigation in line with what Tab already did.
      // Deliberately not applied when docked: a non-modal side panel claiming to be a modal dialog
      // would wrongly hide the genuinely-usable map and list next to it.
      role={isModal ? 'dialog' : undefined}
      aria-modal={isModal ? true : undefined}
      onKeyDown={handleKeyDown}
      style={{ ...asideStyle, outline: 'none' }}
      className={
        (fluidWidth
          ? 'bg-white border-l border-gray-200 z-10'
          : 'bg-white border-l border-gray-200 z-10 fixed inset-0 md:static md:inset-auto w-full md:w-[416px] md:min-w-[416px]'
        ) + ' focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-blue-500'
      }
      aria-label={`Details for ${spokenLabel(site.name)}`}
    >
      {/* Photo strip */}
      {hasFallback ? (
        <div
          data-testid="photo-fallback"
          style={{ ...photoStripStyle, background: siteConfig?.color ?? INGLIS_BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {/* Some marker colors (e.g. amber) are ~3:1 against white, failing WCAG AA for this
              small bold label — pick whichever of white/near-black actually passes 4.5:1. */}
          <span style={{ color: getReadableTextColor(siteConfig?.color ?? INGLIS_BLUE), fontSize: '0.85rem', fontWeight: 700 }}>{siteConfig?.label}</span>
        </div>
      ) : (
        <img
          data-testid="photo-strip"
          src={src}
          onError={onError}
          alt={spokenLabel(site.name)}
          style={{ ...photoStripStyle, objectFit: 'contain', objectPosition: 'center', display: 'block', background: '#f1f5f9' }}
        />
      )}

      {/* Header */}
      <div style={{ padding: '16px 20px 14px', borderBottom: '1px solid #eef2f7' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontWeight: 700, color: 'var(--text-strong)', fontSize: '1.25rem', lineHeight: 1.2, margin: 0 }}><SpokenAT text={site.name} /></h2>
            <p style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', marginTop: 3 }}>{site.org}</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            style={{ flexShrink: 0, width: 32, height: 32, border: '1px solid #dbe2ea', borderRadius: 8, background: '#fff', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            aria-label="Close site panel"
          >
            ✕
          </button>
        </div>

        {/* Address */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, color: 'var(--text-secondary)', fontSize: '0.82rem', marginBottom: 4 }}>
          <PinIcon />
          <span><strong style={{ color: 'var(--text-strong)' }}>{site.region}</strong> · {site.address}</span>
        </div>

        {/* Website */}
        {site.url && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: '0.82rem' }}>
            <LinkIcon />
            <a href={site.url} target="_blank" rel="noopener noreferrer" style={{ color: INGLIS_BLUE, textDecoration: 'none' }}
              onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'}
              onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}
              onFocus={e => e.currentTarget.style.textDecoration = 'underline'}
              onBlur={e => e.currentTarget.style.textDecoration = 'none'}>
              {site.url.replace(/^https?:\/\//, '')}
            </a>
          </div>
        )}

        {/* External map link for aggregated entries — kept in the header so it's visible without scrolling */}
        {site.subSites && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: '0.82rem', marginTop: site.url ? 4 : 0 }}>
            <LinkIcon />
            <a
              href={site.subSites.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: INGLIS_BLUE, fontWeight: 700, textDecoration: 'none' }}
              onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'}
              onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}
              onFocus={e => e.currentTarget.style.textDecoration = 'underline'}
              onBlur={e => e.currentTarget.style.textDecoration = 'none'}
            >
              View TechOWL's full interactive map↗
            </a>
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: '16px 20px' }}>
        {site.audiences.family && <FamilyContent data={site.audiences.family} site={site} />}
      </div>

      {/* Sub-sites — for aggregated entries like AT Reuse Sites */}
      {site.subSites && (
        <div style={{ padding: '0 20px 20px' }}>
          <div style={{ borderTop: '1px solid #eef2f7', paddingTop: 16 }}>
            <h3 style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 8 }}>
              Reuse Programs ({site.subSites.items.length})
            </h3>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[...site.subSites.items].sort((a, b) => a.name.localeCompare(b.name)).map(renderSubSiteItem)}
            </ul>
          </div>
        </div>
      )}
    </aside>
  )
}
